import { useEffect, useState } from "react";

const readFilterValues = <Value extends string>(
    storageKey: string,
    allowedValues: readonly Value[],
): Set<Value> => {
    try {
        const storedValue = window.localStorage.getItem(storageKey);
        if (storedValue === null) {
            return new Set(allowedValues);
        }

        const parsedValue: unknown = JSON.parse(storedValue);
        if (!Array.isArray(parsedValue)) {
            return new Set(allowedValues);
        }

        return new Set(
            parsedValue.filter((value): value is Value =>
                typeof value === "string" && allowedValues.includes(value as Value),
            ),
        );
    } catch {
        return new Set(allowedValues);
    }
};

const usePersistentFilterSet = <Value extends string>(
    storageKey: string,
    allowedValues: readonly Value[],
): [Set<Value>, (value: Value) => void] => {
    const [selectedValues, setSelectedValues] = useState<Set<Value>>(
        () => readFilterValues(storageKey, allowedValues),
    );

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(Array.from(selectedValues)));
        } catch {
            // Filtering should keep working when localStorage is unavailable.
        }
    }, [selectedValues, storageKey]);

    const toggleValue = (value: Value) => {
        setSelectedValues(currentValues => {
            const nextValues = new Set(currentValues);
            if (nextValues.has(value)) {
                nextValues.delete(value);
            } else {
                nextValues.add(value);
            }
            return nextValues;
        });
    };

    return [selectedValues, toggleValue];
};

export default usePersistentFilterSet;
